export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

export class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

export class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

export function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}
window.trapFocus = trapFocus;

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

export function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}
window.focusVisiblePolyfill = focusVisiblePolyfill;

export function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}
window.pauseAllMedia = pauseAllMedia;

export function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}
window.removeTrapFocus = removeTrapFocus;

export function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}
window.onKeyUpEscape = onKeyUpEscape;

export function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
window.debounce = debounce;

export function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}
window.throttle = throttle;

export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}
window.fetchConfig = fetchConfig;

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

export function extractNumberPriceFromText(str) {
  if (typeof str !== 'string') return null;

  let cleaned = str.replace(/[^0-9.,\s]/g, '').trim();

  cleaned = cleaned.replace(/\s/g, '');

  if (cleaned.includes(',') && cleaned.includes('.')) {
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes(',')) {
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      cleaned = cleaned.replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  }

  const num = parseFloat(cleaned);

  return isNaN(num) ? null : num;
}
window.extractNumberPriceFromText = extractNumberPriceFromText;

export function extractCurrency(priceString) {
  const currencyPattern = /[^\d.,\s]+/; // Matches non-numeric, non-decimal, and non-space characters
  const match = priceString.match(currencyPattern);
  return match ? match[0] : null;
}
window.extractCurrency = extractCurrency;

export function getDiscount(quantity) {
  if (!window.discountRules || !Array.isArray(window.discountRules) || window.discountRules.length === 0) {
    console.warn('No discount rules found');
    return 0;
  }
  // Find the first rule matching the provided quantity
  const rule = window.discountRules.find((r) => quantity >= r.min && quantity <= r.max);
  // Return the matching discount or 0 if none found
  return rule ? rule.discount : 0;
}
window.getDiscount = getDiscount;

export function formatMoney(amount, currency = '$') {
  if (amount <= 0) {
    return 'FREE';
  }

  return (
    currency +
    amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}
window.formatMoney = formatMoney;

export function intlFormatMoney(amount, currency = 'USD') {
  const formattedPrice = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: currency,
  }).format(amount);

  return formattedPrice;
}
window.intlFormatMoney = intlFormatMoney;

export function addToCart(variantId, quantity = 1) {
  // Construct the payload for the AJAX request
  const requestData = {
    items: [
      {
        id: variantId,
        quantity: quantity,
      },
    ],
  };

  return new Promise((resolve, reject) => {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestData),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        if (typeof monster_refresh === 'function' && !window.monsterCartBroken) {
          monster_refresh();
        }
        resolve(data);
      })
      .catch((error) => {
        console.error('Add to Cart Error:', error);
        reject(error);
      });
  });
}
window.addToCart = addToCart;

/**
 * Waits for an element matching `targetSelector` to appear inside
 * the element matching `parentSelector`, then calls the callback `cb`.
 *
 * @param {Function} cb - The callback to run once the target element appears.
 * @param {string} parentSelector - The selector for the parent element to observe.
 * @param {string} targetSelector - The selector for the target element to wait for.
 */
export function waitForElement(cb, parentSelector, targetSelector, delayToNextTick = false) {
  const parent = document.querySelector(parentSelector);

  if (delayToNextTick) {
    const _cb = cb;
    cb = (...args) => setTimeout(() => _cb(...args), Number.isInteger(+delayToNextTick) ? +delayToNextTick : 0);
  }

  // If the parent element doesn't exist, log a warning and exit
  if (!parent) {
    console.warn(`Parent element not found for selector: "${parentSelector}"`);
    return;
  }

  // Check if the target element is already present
  if (parent.querySelector(targetSelector)) {
    cb(parent, parent.querySelector(targetSelector));
    return;
  }

  // Create a MutationObserver to watch for changes in the parent
  const observer = new MutationObserver((mutationsList, observerInstance) => {
    const target = parent.querySelector(targetSelector);

    if (!target) {
      return;
    }

    // If the target element is found, disconnect the observer and call cb
    observerInstance.disconnect();
    cb(parent, target);
  });

  // Observe the parent for when new child elements are added
  observer.observe(parent, { childList: true, subtree: true });
}
window.waitForElement = waitForElement;

export function pluralize(count, singular, plural) {
  return count === 1 ? singular : plural;
}
window.pluralize = pluralize;

export function getFirstIntegerFromString(str) {
  // Match the first sequence of digits in the string.
  const match = str.match(/\d+/);

  // If a match is found, parse it as a number; otherwise, default to null.
  const firstNumber = match ? parseInt(match[0], 10) : null;

  return firstNumber;
}
window.getFirstIntegerFromString = getFirstIntegerFromString;

export function getOwnTextContent(element) {
  let ownText = '';
  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      ownText += node.textContent;
    }
  }
  return ownText.trim();
}
window.getOwnTextContent = getOwnTextContent;

export function onElementClassChanged(element, callback) {
  if (!(element instanceof HTMLElement)) {
    throw new Error('Invalid element provided');
  }

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.attributeName === 'class') {
        callback(element.classList);
      }
    }
  });

  observer.observe(element, { attributes: true, attributeFilter: ['class'] });

  return () => observer.disconnect(); // Return a function to stop observing when needed
}
window.onElementClassChanged = onElementClassChanged;

function updateViewportUnits() {
  const header = document.querySelector('.section-header');

  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

  document.documentElement.style.setProperty(
    '--header-bottom-position',
    `${parseInt(header.getBoundingClientRect().bottom)}px`
  );
}

window.addEventListener('resize', function () {
  updateViewportUnits();
});

updateViewportUnits();

/**
 * Observe an element and react when it becomes visible in the viewport.
 *
 * @param {Element}  el        The element to watch.
 * @param {Function} cb        Callback to run (receives the IntersectionObserverEntry).
 *                              If you prefer events, pass null here and use the `eventName` option.
 * @param {Object}   [opts]    Optional settings.
 * @param {boolean}  [opts.once=true]       Call only the first time the element is seen.
 * @param {number}   [opts.threshold=0]     Portion of the element that must be visible (0-1).
 * @param {string}   [opts.eventName='in-viewport']
 *                                        Name of the custom event to dispatch if cb is null.
 * @returns {Function}                     Call this to stop observing.
 */
export function whenInViewport(el, cb, opts = {}) {
  const { once = true, threshold = 0, eventName = 'in-viewport', onlyWhenIntersecting = true } = opts;

  if (!el) throw new Error('Element required');

  // Fallback for very old browsers
  if (!('IntersectionObserver' in window)) {
    const handler = () => {
      if (isInViewport(el, threshold)) {
        trigger(el);
        if (once) window.removeEventListener('scroll', handler, true);
      }
    };
    window.addEventListener('scroll', handler, true);
    // run immediately in case element is already visible
    handler();
    return () => window.removeEventListener('scroll', handler, true);
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || !onlyWhenIntersecting) {
          trigger(el, entry);
          if (once) io.disconnect();
        }
      });
    },
    { threshold }
  );

  io.observe(el);

  // Helper to stop observing manually
  return () => io.disconnect();

  /** Internal: fire callback or event */
  function trigger(target, entry) {
    if (typeof cb === 'function') {
      cb(entry);
    } else {
      target.dispatchEvent(new CustomEvent(eventName, { detail: entry }));
    }
  }
}
window.whenInViewport = whenInViewport;

function fillSwiperToFullWidth(swiperEl) {
  const wrapper = swiperEl.querySelector('.swiper-wrapper');
  const slides = Array.from(wrapper.children);
  const slideCount = slides.length;

  // Clone until full width
  const containerWidth = window.innerWidth;
  let currentWidth = wrapper.scrollWidth;

  let i = 0;
  while (currentWidth < containerWidth && i < 50) {
    // avoid infinite loop
    const clone = slides[i % slideCount].cloneNode(true);
    wrapper.appendChild(clone);
    currentWidth = wrapper.scrollWidth;
    i++;
  }
}

window.fillSwiperToFullWidth = fillSwiperToFullWidth;

function autoAnimateHeight(el, options = {}) {
  const { duration = 300, easing = 'ease' } = options;

  let lastHeight = el.offsetHeight;
  let isTransitioning = false;

  const observer = new ResizeObserver(() => {
    const newHeight = el.scrollHeight;
    if (newHeight === lastHeight || isTransitioning || Math.abs(newHeight - lastHeight) < 5) return;

    isTransitioning = true;

    // Freeze current height
    el.style.height = `${lastHeight}px`;
    el.style.overflow = 'hidden';

    // Force reflow
    void el.offsetHeight;

    // Apply transition
    el.style.transition = `height ${duration}ms ${easing}`;
    el.style.height = `${newHeight}px`;

    const onTransitionEnd = () => {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      lastHeight = el.offsetHeight;
      isTransitioning = false;

      el.removeEventListener('transitionend', onTransitionEnd);
    };

    el.addEventListener('transitionend', onTransitionEnd);
  });

  observer.observe(el);
  return () => observer.disconnect(); // Call this to stop observing
}

window.autoAnimateHeight = autoAnimateHeight;

document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll('.js-auto-animate-height');
  elements.forEach((el) => {
    autoAnimateHeight(el);
  });
});

document.addEventListener('DOMContentLoaded', () => {
  window.addEventListener(
    'scroll',
    throttle(() => {
      document.querySelectorAll('[data-sticky], :has(> [data-parent-sticky])').forEach((el) => {
        const stickyTop = parseInt(window.getComputedStyle(el).top);
        const currentTop = el.getBoundingClientRect().top;
        el.classList.toggle('is-sticky', Math.abs(currentTop - stickyTop) < 1);
      });
    }),
    100
  );
});

export function removeCartAttributes(...attributeNames) {
  return new Promise((resolve, reject) => {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: attributeNames.reduce((acc, name) => {
          acc[name] = '';
          return acc;
        }, {}),
      }),
    })
      .then((response) => response.json())
      .then((cart) => {
        resolve(cart);
      })
      .catch((error) => reject('Error removing attributes:', error));
  });
}
window.removeCartAttributes = removeCartAttributes;

export function removeCartItem(identifier) {
  return new Promise((resolve, reject) => {
    // Step 1: Get the current cart
    fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then((res) => res.json())
      .then((cart) => {
        // Step 2: Find matching item by id, name, or handle
        const lineItem = cart.items.find((item) => {
          return (
            item.id == identifier || // match by variant ID
            item.product_title?.toLowerCase() === String(identifier).toLowerCase() || // match by name
            item.handle?.toLowerCase() === String(identifier).toLowerCase() // match by handle (if available)
          );
        });

        if (!lineItem) {
          reject(`Cart item with identifier "${identifier}" not found.`);
          return;
        }

        // Step 3: Remove item by setting quantity to 0
        fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: lineItem.key, // Shopify's unique key for line item
            quantity: 0,
          }),
        })
          .then((res) => res.json())
          .then((updatedCart) => resolve(updatedCart))
          .catch((err) => reject(`Error removing cart item: ${err}`));
      })
      .catch((err) => reject(`Error fetching cart: ${err}`));
  });
}

window.removeCartItem = removeCartItem;
